import { ShieldCheck } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { api, setAccessToken, unwrap } from '@/api/client';
import { recaptchaHeader, recaptchaToken } from '@/auth/recaptcha';
import { ApiError, mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { InputOTP, InputOTPSlot } from '@/components/ui/input-otp';

type StepUpResult = components['schemas']['StaffStepUpResult'];
interface Reto {
  challengeToken: string;
  emailMasked: string;
  resolve: () => void;
  reject: (e: Error) => void;
}

/**
 * Acciones sensibles (borrar alumnos, exportar datos personales, editar staff, llaves de IA): si la API responde
 * `STEP_UP_REQUIRED`, pide el código que llega por correo, renueva el token y reintenta.
 * Uso: `const { proteger, dialogo } = useStepUp(); await proteger(() => accion());` y `{dialogo}`.
 */
export function useStepUp(): {
  proteger: <T>(fn: () => Promise<T>) => Promise<T>;
  dialogo: ReactNode;
} {
  const [reto, setReto] = useState<Reto | null>(null);
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const proteger = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'STEP_UP_REQUIRED')) throw e;
      const token = await recaptchaToken('admin_step_up');
      const r = await unwrap<StepUpResult>(
        api.POST('/api/v1/admin/auth/step-up', { headers: recaptchaHeader(token) }),
      );
      if (r.status === 'verified') {
        setAccessToken(r.accessToken);
        return fn();
      }
      await new Promise<void>((resolve, reject) => {
        setCodigo('');
        setReto({ challengeToken: r.challengeToken, emailMasked: r.emailMasked, resolve, reject });
      });
      return fn();
    }
  }, []);

  const verificar = async (code: string) => {
    if (!reto || code.length !== 6) return;
    setEnviando(true);
    try {
      const r = await unwrap<StepUpResult>(
        api.POST('/api/v1/admin/auth/step-up/verify', {
          body: { challengeToken: reto.challengeToken, code },
        }),
      );
      if (r.status !== 'verified')
        throw new Error('No pudimos verificar el código. Inténtalo de nuevo.');
      setAccessToken(r.accessToken);
      reto.resolve();
      setReto(null);
    } catch (e) {
      toast.error(mensajeDe(e));
      setCodigo('');
    } finally {
      setEnviando(false);
    }
  };

  const dialogo = (
    <Dialog
      open={reto !== null}
      onOpenChange={(o) => {
        if (!o && reto) {
          reto.reject(new Error('Cancelaste la verificación.'));
          setReto(null);
        }
      }}
    >
      <DialogContent>
        <ShieldCheck className="text-cian-texto" size={32} />
        <DialogTitle>Confirma que eres tú</DialogTitle>
        <DialogDescription>
          Por seguridad, escribe el código de 6 dígitos que enviamos a{' '}
          <strong>{reto?.emailMasked}</strong>.
        </DialogDescription>
        <div className="my-5 flex justify-center">
          <InputOTP
            maxLength={6}
            value={codigo}
            onChange={setCodigo}
            onComplete={(v: string) => void verificar(v)}
            disabled={enviando}
            autoFocus
            inputMode="numeric"
            pattern="^[0-9]*$"
            aria-label="Código de verificación"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} />
            ))}
          </InputOTP>
        </div>
      </DialogContent>
    </Dialog>
  );

  return { proteger, dialogo };
}
